import MainLayout from '@/views/MainLayout'
import Home from '@/views/Home'
import store from '@/store'
import * as PN from '@/utils/PermissionNames'
import AppNotFound from '@/components/core/AppNotFound'
import AppLocked from '@/components/core/AppLocked'
import { DOMAIN_WORKSPACE, DOMAIN_TOP, DOMAIN_ANY } from './helpers'
import VueRouter from 'vue-router'
import { redirectLocation } from './navigation'
import { opensOnMap } from '@/components/home/landing'

export const pages = [
  {
    path: '/',
    component: MainLayout,
    children: [
      {
        path: '',
        name: 'home',
        meta: { requireSigned: true, requireDomain: DOMAIN_WORKSPACE },
        component: Home,
        // the app may start on the map instead (see landing.js). Only its first navigation:
        // "Home" in the sidenav, back / forward and every redirect later on land here
        beforeEnter: async (to, from, next) => {
          if (from === VueRouter.START_LOCATION && (await opensOnMap())) next(redirectLocation(to, { name: 'map-home' }))
          else next()
        }
      },
      {
        path: 'map',
        name: 'map-home',
        meta: { requireSigned: true, requireDomain: DOMAIN_WORKSPACE },
        component: () => import(/* webpackChunkName: "map" */ '../views/MapHome.vue'),
        children: [
          {
            path: ':floorId',
            name: 'map',
            meta: { requireSigned: true, requireDomain: DOMAIN_WORKSPACE },
            component: () => import(/* webpackChunkName: "map" */ '../views/MapFloor.vue')
          }
        ]
      },
      {
        path: 'floors',
        name: 'floors',
        meta: { requireSigned: true, requireDomain: DOMAIN_WORKSPACE },
        component: () => import(/* webpackChunkName: "view" */ '../views/FloorsList.vue'),
        beforeEnter: (to, from, next) => {
          store.getters['rbac/can'](PN.FLOOR_UPDATE) ? next() : next(redirectLocation(to, { name: 'home' }))
        },
        children: [
          {
            path: ':floorId',
            name: 'floor-edit',
            meta: { requireSigned: true, requireDomain: DOMAIN_WORKSPACE },
            component: () => import(/* webpackChunkName: "view" */ '../views/FloorEdit.vue')
          }
        ]
      },
      {
        path: '/settings',
        name: 'settings',
        meta: { requireSigned: true, requireDomain: DOMAIN_WORKSPACE },
        beforeEnter: (to, from, next) => {
          store.getters['rbac/can'](PN.SETTINGS_CRUD) ? next() : next(redirectLocation(to, { name: 'home' }))
        },
        component: () => import(/* webpackChunkName: "view" */ '../views/Settings.vue')
      },
      {
        path: '/entity-search',
        name: 'entity-search',
        meta: { requireSigned: true, requireDomain: DOMAIN_WORKSPACE },
        component: () => import(/* webpackChunkName: "view" */ '../views/EntitySearch.vue')
      },
      {
        path: '/point-icons',
        name: 'point-icons',
        meta: { requireSigned: true, requireDomain: DOMAIN_WORKSPACE },
        component: () => import(/* webpackChunkName: "view" */ '../views/PointIcons.vue'),
        beforeEnter: (to, from, next) => {
          store.getters['rbac/can'](PN.POINT_UPDATE) ||
          store.getters['rbac/can'](PN.POINT_CREATE) ||
          store.getters['rbac/can'](PN.POINT_DELETE)
            ? next()
            : next(redirectLocation(to, { name: 'home' }))
        },
        children: [
          {
            path: ':pointIconId',
            name: 'point-icon-edit',
            meta: { requireSigned: true, requireDomain: DOMAIN_WORKSPACE },
            component: () => import(/* webpackChunkName: "view" */ '../views/PointIcon.vue')
          }
        ]
      },
      {
        path: '/users',
        name: 'users',
        meta: { requireSigned: true, requireDomain: DOMAIN_WORKSPACE },
        component: () => import(/* webpackChunkName: "view" */ '../views/Users.vue'),
        beforeEnter: (to, from, next) => {
          store.getters['rbac/can'](PN.USER_CRUD) ? next() : next(redirectLocation(to, { name: 'home' }))
        },
        children: [
          {
            path: ':userId',
            name: 'user-edit',
            meta: { requireSigned: true, requireDomain: DOMAIN_WORKSPACE },
            component: () => import(/* webpackChunkName: "view" */ '../views/User.vue')
          }
        ]
      },
      {
        path: '/employees',
        name: 'employees',
        meta: { requireSigned: true, requireDomain: DOMAIN_WORKSPACE },
        component: () => import(/* webpackChunkName: "view" */ '../views/Employees.vue'),
        children: [
          {
            path: ':employeeId',
            name: 'employee',
            meta: { requireSigned: true, requireDomain: DOMAIN_WORKSPACE },
            component: () => import(/* webpackChunkName: "view" */ '../views/Employee.vue')
          }
        ]
      },
      {
        path: '/employees-import',
        name: 'employees-import',
        meta: { requireSigned: true, requireDomain: DOMAIN_WORKSPACE },
        component: () => import(/* webpackChunkName: "view" */ '../views/EmployeesImport.vue')
      },
      {
        path: '/bookings',
        name: 'bookings',
        meta: { requireSigned: true, requireDomain: DOMAIN_WORKSPACE },
        component: () => import(/* webpackChunkName: "view" */ '../views/Bookings.vue')
      }
    ]
  },
  {
    path: '/auth',
    name: 'auth',
    component: () => import(/* webpackChunkName: "auth" */ '../views/auth/AuthLayout.vue'),
    beforeEnter(to, from, next) {
      to.name === 'auth' ? next(redirectLocation(to, { name: 'register' })) : next()
    },
    children: [
      {
        path: '/login-intranet',
        name: 'login-intranet',
        meta: { requireSigned: false, requireDomain: DOMAIN_ANY },
        component: () => import(/* webpackChunkName: "auth" */ '../views/auth/LoginIntranet.vue')
      },
      {
        path: '/login',
        name: 'login',
        meta: { requireSigned: false, requireDomain: DOMAIN_ANY },
        component: () => import(/* webpackChunkName: "auth" */ '../views/auth/Login.vue')
      },
      {
        path: '/login-ms',
        name: 'login-ms',
        meta: { requireSigned: false, requireDomain: DOMAIN_ANY },
        component: () => import(/* webpackChunkName: "auth" */ '../views/auth/LoginMs.vue')
      },
      {
        path: '/register',
        name: 'register',
        meta: { requireSigned: false, requireDomain: DOMAIN_TOP },
        component: () => import(/* webpackChunkName: "auth" */ '../views/auth/Register.vue')
      },
      {
        path: '/register-verification',
        name: 'register-verification',
        // requireSigned intentionally omitted: the guard sends a signed-in user of an unverified
        // workspace here, and requireSigned: false would send them back home - and home here again
        meta: { requireDomain: DOMAIN_TOP },
        component: () => import(/* webpackChunkName: "auth" */ '../views/auth/RegisterVerification.vue')
      },
      {
        path: '/password-reset-request',
        name: 'password-reset-request',
        meta: { requireSigned: false, requireDomain: DOMAIN_TOP },
        component: () => import(/* webpackChunkName: "auth" */ '../views/auth/PasswordResetRequest.vue')
      },
      {
        path: '/password-reset',
        name: 'password-reset',
        meta: { requireSigned: false, requireDomain: DOMAIN_WORKSPACE, requireWorkspaceLoading: false },
        component: () => import(/* webpackChunkName: "auth" */ '../views/auth/PasswordReset.vue')
      }
    ]
  },
  {
    path: '/booking-confirm',
    name: 'booking-confirm',
    // requireSigned intentionally omitted: setting it to false would redirect signed-in users to home (helpers.js:63-64)
    meta: { requireDomain: DOMAIN_WORKSPACE, requireWorkspaceLoading: false },
    component: () => import(/* webpackChunkName: "booking-confirm" */ '../views/BookingConfirm.vue')
  },
  {
    // the catch-all is the child, not the parent: a '*' parent with '' children builds the
    // address back with a trailing slash (/users/), and the screens keep the address they replace
    path: '',
    component: MainLayout,
    children: [
      {
        path: '*',
        name: '404',
        component: AppNotFound
      },
      {
        path: '*',
        name: 'lock',
        component: AppLocked
      }
    ]
  }
]
